import type { StyleResolverContext, CssFragment } from './index';

type GroupHoverDisplayValue = 'block' | 'flex' | 'none';

// ============================================================================
// Types
// ============================================================================

export interface BoxMiscProps {
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  zIndex?: number;
  opacity?: number;
  cursor?: string;
  transform?: string;
  /** Explicit transition; when undefined and bgHover is present, uses background-color transition. */
  transition?: string;
  groupHoverDisplay?: GroupHoverDisplayValue;
}

// ============================================================================
// Resolution helpers
// ============================================================================

/** Returns CSS fragments for text-align, z-index, opacity, cursor, transform, transition, and group-hover display. */
const resolveMiscCss = (props: BoxMiscProps, hasBgHover: boolean): CssFragment[] => {
  const transition =
    props.transition !== undefined ? props.transition : hasBgHover ? 'background-color 150ms ease-in-out' : undefined;
  return [
    props.textAlign ? `text-align: ${props.textAlign};` : null,
    props.zIndex !== undefined ? `z-index: ${props.zIndex};` : null,
    props.opacity !== undefined ? `opacity: ${props.opacity};` : null,
    props.cursor ? `cursor: ${props.cursor};` : null,
    props.transform ? `transform: ${props.transform};` : null,
    transition ? `transition: ${transition};` : null,
    props.groupHoverDisplay ? `[data-group]:hover & {\n  display: ${props.groupHoverDisplay};\n}` : null,
  ];
};

// ============================================================================
// Picker + style resolver
// ============================================================================

const pickMiscProps = <T extends Partial<BoxMiscProps>>(props: T): BoxMiscProps => {
  const { textAlign, zIndex, opacity, cursor, transform, transition, groupHoverDisplay } = props;
  return { textAlign, zIndex, opacity, cursor, transform, transition, groupHoverDisplay };
};

export const resolveBoxMiscStyleCss = (props: Record<string, unknown>, _theme: StyleResolverContext): CssFragment[] => {
  const hasBgHover = Boolean((props as Record<string, unknown>).bgHover);
  return resolveMiscCss(pickMiscProps(props as Partial<BoxMiscProps>), hasBgHover);
};
